/* Public passwordless client signing page — token-scoped.
   Route: /sign/:token
   Backing endpoints under /api/public/agreement/sign/{raw_token}
     GET  → client-safe agreement projection
     POST /confirm-details      body: {correct: bool}
     POST /report-correction    body: {field, detail, correction?}
     POST /                     body: {signature_representation,
                                        signature_method,
                                        confirmed_identity,
                                        create_op_id?}
*/
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { usePageMeta } from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL;

function StatusPill({ children, tone = "neutral" }) {
    const tones = {
        neutral: "bg-stone-100 text-stone-700",
        good: "bg-emerald-100 text-emerald-800",
        warn: "bg-amber-100 text-amber-800",
        bad: "bg-rose-100 text-rose-800",
    };
    return (
        <span className={`inline-block rounded-full px-3 py-0.5 text-xs font-medium ${tones[tone]}`}>
            {children}
        </span>
    );
}

function DrawnSignature({ onChange }) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef({ x: 0, y: 0 });

    const start = (e) => {
        drawingRef.current = true;
        const pt = getPoint(e);
        lastRef.current = pt;
    };
    const move = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const pt = getPoint(e);
        ctx.strokeStyle = "#1c1917";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
        lastRef.current = pt;
    };
    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        onChange(canvasRef.current.toDataURL("image/png"));
    };
    const getPoint = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return {
            x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width),
            y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height),
        };
    };
    const clear = () => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        onChange("");
    };
    return (
        <div>
            <canvas
                ref={canvasRef}
                width={520}
                height={140}
                data-testid="sign-canvas"
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
                className="w-full h-32 rounded border border-stone-300 bg-white cursor-crosshair"
            />
            <div className="mt-1 flex justify-between text-xs text-stone-500">
                <span>Sign inside the box using your mouse, trackpad or finger.</span>
                <button type="button" onClick={clear}
                    data-testid="sign-canvas-clear"
                    className="text-stone-700 underline">
                    Clear
                </button>
            </div>
        </div>
    );
}

export default function PublicSignAgreement() {
    usePageMeta({ title: "Signing | Studio G4", robots: "noindex,nofollow" });
    const { token } = useParams();
    const [row, setRow] = useState(null);
    const [error, setError] = useState("");
    const [msg, setMsg] = useState("");

    const [detailsView, setDetailsView] = useState("idle"); // idle | correcting | confirmed
    const [correction, setCorrection] = useState({ field: "property_address", detail: "" });

    const [method, setMethod] = useState("typed");
    const [typedName, setTypedName] = useState("");
    const [drawnDataUrl, setDrawnDataUrl] = useState("");
    const [confirmedIdentity, setConfirmedIdentity] = useState("");

    const [signing, setSigning] = useState(false);
    const [done, setDone] = useState(null); // {signer_state, agreement_status, all_clients_signed}

    const load = async () => {
        try {
            const r = await fetch(`${API}/api/public/agreement/sign/${token}`);
            if (!r.ok) {
                if (r.status === 410) setError("This link has expired or the agreement has been superseded.");
                else if (r.status === 404) setError("This signing link is not valid.");
                else setError(await r.text() || "Unable to load your agreement.");
                return;
            }
            setRow(await r.json());
        } catch (e) { setError("Network error — please try again."); }
    };
    useEffect(() => { load(); }, [token]);

    const submitDetailsConfirm = async () => {
        setMsg("");
        try {
            const r = await fetch(
                `${API}/api/public/agreement/sign/${token}/confirm-details`,
                { method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ correct: true }) });
            if (!r.ok) { setError(await r.text() || "Unable to confirm."); return; }
            setDetailsView("confirmed");
            setMsg("Client details confirmed.");
        } catch (e) { setError("Network error."); }
    };

    const submitCorrection = async () => {
        setMsg("");
        if (!correction.detail.trim()) { setError("Please describe the correction."); return; }
        try {
            const r = await fetch(
                `${API}/api/public/agreement/sign/${token}/report-correction`,
                { method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(correction) });
            if (!r.ok) { setError(await r.text() || "Unable to submit correction."); return; }
            setDetailsView("idle");
            setMsg("Correction request submitted. LRS will review it before you sign.");
            setCorrection({ field: "property_address", detail: "" });
        } catch (e) { setError("Network error."); }
    };

    const submitSign = async () => {
        setError(""); setMsg("");
        const rep = method === "typed" ? typedName.trim() : drawnDataUrl;
        if (!rep) { setError("Please provide your signature."); return; }
        if (!confirmedIdentity.trim()) {
            setError("Please confirm your full legal name."); return;
        }
        setSigning(true);
        try {
            const r = await fetch(
                `${API}/api/public/agreement/sign/${token}`,
                { method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                      signature_representation: rep,
                      signature_method: method,
                      confirmed_identity: confirmedIdentity.trim(),
                      create_op_id: `pub-sign-${Date.now()}`,
                  }) });
            if (!r.ok) { setError(await r.text() || "Signing failed."); setSigning(false); return; }
            setDone(await r.json());
        } catch (e) { setError("Network error."); }
        setSigning(false);
    };

    if (error && !row) {
        return (
            <div className="mx-auto my-16 max-w-2xl px-6">
                <h1 className="text-2xl font-semibold text-stone-900">Free Design Agreement</h1>
                <p className="mt-4 rounded border border-rose-200 bg-rose-50 p-4 text-rose-800"
                    data-testid="sign-load-error">{error}</p>
            </div>
        );
    }
    if (!row) {
        return (
            <div className="mx-auto my-16 max-w-2xl px-6 text-stone-500">Loading…</div>
        );
    }

    if (done) {
        const onboarding = done.onboarding_status || {};
        const message = onboarding.message
            || (done.all_clients_signed
                ? "All required client signatures are now in place. Studio G4 Ltd will countersign the agreement next."
                : "Thank you — your signature has been recorded. Other required client signatures are still outstanding.");
        const heading = onboarding.state === "client_part_complete"
            ? "Your part is complete"
            : "Thank you — signature recorded";
        return (
            <div className="mx-auto my-16 max-w-2xl px-6" data-testid="sign-thank-you">
                <h1 className="text-2xl font-semibold text-stone-900">{heading}</h1>
                <p className="mt-3 text-stone-700">
                    Your signature on <strong>{row.agreement_reference}</strong> (v{row.agreement_version})
                    has been recorded.
                </p>
                <div className="mt-4 rounded border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700"
                    data-testid="sign-remaining-status"
                    data-onboarding-state={onboarding.state || "unknown"}>
                    {message}
                </div>
            </div>
        );
    }

    const client = (row.contracting_parties && row.contracting_parties.client) || {};
    const projectContext = row.project_context || {
        property_address: client.property_address,
        primary_contact_name: client.name,
    };
    const scope = row.free_design_scope || {};
    const signer = row.signer || {};
    const roleLabel = (signer.role || "").replace(/_/g, " ");
    const isPrimarySigner = signer.role === "primary_client";

    return (
        <div className="mx-auto my-10 max-w-3xl px-6" data-testid="sign-page">
            <header className="border-b border-stone-200 pb-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold text-stone-900">Free Design Agreement</h1>
                    <StatusPill tone="neutral">v{row.agreement_version}</StatusPill>
                </div>
                <p className="mt-1 text-sm text-stone-600">
                    Reference <strong data-testid="sign-agreement-ref">{row.agreement_reference}</strong> ·
                    Studio G4 Ltd
                </p>
                {signer.name && (
                    <p className="mt-3 text-sm text-stone-700" data-testid="sign-signer-heading">
                        Signing as <strong>{signer.name}</strong> ({signer.role?.replace(/_/g, " ")})
                    </p>
                )}
            </header>

            {msg && (
                <p className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
                    data-testid="sign-msg">{msg}</p>
            )}
            {error && (
                <p className="mt-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
                    data-testid="sign-err">{error}</p>
            )}

            {/* Section 1 — signer identity (own) */}
            <section className="mt-8">
                <h2 className="text-lg font-medium text-stone-900">1. Confirm your details</h2>
                <p className="mt-1 text-sm text-stone-500">
                    These are the details we hold for you as a signer of this agreement.
                </p>
                <div className="mt-3 rounded border border-stone-200 bg-white p-4">
                    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2" data-testid="sign-signer-identity">
                        <div>
                            <dt className="text-stone-500">Your name</dt>
                            <dd className="text-stone-900" data-testid="sign-signer-name">{signer.name || "—"}</dd>
                        </div>
                        <div>
                            <dt className="text-stone-500">Your email</dt>
                            <dd className="text-stone-900" data-testid="sign-signer-email">{signer.email || "—"}</dd>
                        </div>
                        <div>
                            <dt className="text-stone-500">Your role</dt>
                            <dd className="text-stone-900" data-testid="sign-signer-role">{roleLabel || "—"}</dd>
                        </div>
                    </dl>
                    {detailsView !== "correcting" && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={submitDetailsConfirm}
                                data-testid="sign-details-confirm"
                                className="rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-800">
                                These details are correct
                            </button>
                            <button
                                type="button"
                                onClick={() => setDetailsView("correcting")}
                                data-testid="sign-details-correct"
                                className="rounded border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50">
                                Report a correction
                            </button>
                            {detailsView === "confirmed" && (
                                <StatusPill tone="good">Confirmed</StatusPill>
                            )}
                        </div>
                    )}
                    {detailsView === "correcting" && (
                        <div className="mt-4 space-y-3" data-testid="sign-correction-form">
                            <label className="block text-sm text-stone-700">
                                Which detail needs correcting?
                                <select
                                    value={correction.field}
                                    data-testid="sign-correction-field"
                                    onChange={(e) => setCorrection({ ...correction, field: e.target.value })}
                                    className="mt-1 block w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm">
                                    <option value="signer_name">My name</option>
                                    <option value="signer_email">My email</option>
                                    <option value="property_address">Property address</option>
                                    <option value="primary_contact">Primary project contact</option>
                                    <option value="other">Other</option>
                                </select>
                            </label>
                            <label className="block text-sm text-stone-700">
                                Please describe the correction
                                <textarea
                                    value={correction.detail}
                                    data-testid="sign-correction-detail"
                                    onChange={(e) => setCorrection({ ...correction, detail: e.target.value })}
                                    rows={3}
                                    className="mt-1 block w-full rounded border border-stone-300 px-3 py-2 text-sm"
                                    placeholder="e.g. My email is spelt with two 'l's." />
                            </label>
                            <div className="flex gap-2">
                                <button type="button" onClick={submitCorrection}
                                    data-testid="sign-correction-submit"
                                    className="rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-800">
                                    Submit correction request
                                </button>
                                <button type="button" onClick={() => setDetailsView("idle")}
                                    className="rounded border border-stone-300 px-4 py-2 text-sm">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Section 2 — shared project details */}
            <section className="mt-8">
                <h2 className="text-lg font-medium text-stone-900">2. Project details</h2>
                <p className="mt-1 text-sm text-stone-500">
                    These describe the project this agreement covers.
                </p>
                <div className="mt-3 rounded border border-stone-200 bg-white p-4">
                    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2" data-testid="sign-project-context">
                        <div className="sm:col-span-2">
                            <dt className="text-stone-500">Property address</dt>
                            <dd className="text-stone-900" data-testid="sign-property-address">
                                {projectContext.property_address || "—"}
                            </dd>
                        </div>
                        <div className="sm:col-span-2">
                            <dt className="text-stone-500">
                                {isPrimarySigner ? "Primary contact" : "Primary contact on this project"}
                            </dt>
                            <dd className="text-stone-900" data-testid="sign-primary-contact">
                                {projectContext.primary_contact_name || "—"}
                                {!isPrimarySigner && signer.name && projectContext.primary_contact_name
                                    && projectContext.primary_contact_name !== signer.name && (
                                    <span className="ml-2 text-stone-500">
                                        (you are signing as <strong>{signer.name}</strong>)
                                    </span>
                                )}
                            </dd>
                        </div>
                    </dl>
                </div>
            </section>

            {/* Section 3 — Free Design Scope */}
            <section className="mt-8">
                <h2 className="text-lg font-medium text-stone-900">3. Free Design Scope</h2>
                <div className="mt-3 rounded border border-stone-200 bg-white p-4 text-sm text-stone-800"
                    data-testid="sign-scope">
                    {scope.included && scope.included.length > 0 && (
                        <>
                            <p className="font-medium">Included</p>
                            <ul className="mt-1 list-disc pl-5">
                                {scope.included.map((s, i) => <li key={`i-${i}`}>{s}</li>)}
                            </ul>
                        </>
                    )}
                    {scope.exclusions && scope.exclusions.length > 0 && (
                        <>
                            <p className="mt-3 font-medium">Exclusions</p>
                            <ul className="mt-1 list-disc pl-5">
                                {scope.exclusions.map((s, i) => <li key={`e-${i}`}>{s}</li>)}
                            </ul>
                        </>
                    )}
                    {scope.assumptions && scope.assumptions.length > 0 && (
                        <>
                            <p className="mt-3 font-medium">Assumptions</p>
                            <ul className="mt-1 list-disc pl-5">
                                {scope.assumptions.map((s, i) => <li key={`a-${i}`}>{s}</li>)}
                            </ul>
                        </>
                    )}
                    <p className="mt-3 text-stone-700">
                        Included revision allowance: <strong>{row.included_revision_allowance}</strong>
                    </p>
                </div>
            </section>

            {/* Section 4 — Agreement */}
            <section className="mt-8">
                <h2 className="text-lg font-medium text-stone-900">4. Review the Free Design Agreement</h2>
                <div className="mt-3 rounded border border-stone-200 bg-white p-4 text-sm text-stone-800"
                    data-testid="sign-agreement-body">
                    <p>
                        This agreement records that Studio G4 Ltd will provide the
                        complimentary Free Design work described in section&nbsp;2 above, subject to the
                        included revision allowance. Signing this agreement <strong>does not</strong>{" "}
                        commit you to appointing Studio G4 Ltd for any subsequent
                        paid services.
                    </p>
                    <p className="mt-2 text-stone-500 text-xs">
                        Content hash: {(row.generated_document && row.generated_document.content_hash) || "—"}
                    </p>
                </div>
            </section>

            {/* Section 5 — Sign */}
            <section className="mt-8">
                <h2 className="text-lg font-medium text-stone-900">5. Sign</h2>
                <div className="mt-3 rounded border border-stone-200 bg-white p-4">
                    <div className="flex gap-3 text-sm">
                        <label className={`rounded border px-3 py-1 cursor-pointer ${method === "typed" ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300"}`}>
                            <input type="radio" name="method" value="typed"
                                data-testid="sign-method-typed"
                                checked={method === "typed"}
                                onChange={() => setMethod("typed")}
                                className="hidden" />
                            Typed signature
                        </label>
                        <label className={`rounded border px-3 py-1 cursor-pointer ${method === "drawn" ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300"}`}>
                            <input type="radio" name="method" value="drawn"
                                data-testid="sign-method-drawn"
                                checked={method === "drawn"}
                                onChange={() => setMethod("drawn")}
                                className="hidden" />
                            Drawn signature
                        </label>
                    </div>

                    <div className="mt-4">
                        {method === "typed" ? (
                            <label className="block text-sm text-stone-700">
                                Type your full name to sign
                                <input
                                    value={typedName}
                                    data-testid="sign-typed-input"
                                    onChange={(e) => setTypedName(e.target.value)}
                                    className="mt-1 block w-full rounded border border-stone-300 px-3 py-2 font-serif text-lg italic"
                                    placeholder="Your full legal name" />
                            </label>
                        ) : (
                            <DrawnSignature onChange={setDrawnDataUrl} />
                        )}
                    </div>

                    <label className="mt-4 block text-sm text-stone-700">
                        Please confirm your full legal name
                        <input
                            value={confirmedIdentity}
                            data-testid="sign-identity-input"
                            onChange={(e) => setConfirmedIdentity(e.target.value)}
                            className="mt-1 block w-full rounded border border-stone-300 px-3 py-2 text-sm" />
                    </label>

                    <div className="mt-4 flex items-center gap-3">
                        <button
                            type="button"
                            disabled={signing}
                            onClick={submitSign}
                            data-testid="sign-submit"
                            className="rounded bg-emerald-700 px-5 py-2 text-sm text-white hover:bg-emerald-800 disabled:opacity-60">
                            {signing ? "Signing…" : "Submit signature"}
                        </button>
                        <p className="text-xs text-stone-500">
                            Your signature will be bound to content hash{" "}
                            <span className="font-mono">{(row.generated_document && row.generated_document.content_hash || "").slice(0, 16)}…</span>
                        </p>
                    </div>
                </div>
            </section>

            <footer className="mt-10 text-center text-xs text-stone-500">
                Secure signing link · Studio G4 Ltd ·
                Contact <a className="underline" href="mailto:info@studio-g4.co.uk">info@studio-g4.co.uk</a>
            </footer>
        </div>
    );
}
